// src/modules/time-tracking/LiveMap.js (admin) — who's clocked in right now.
import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { FaUserClock, FaSyncAlt, FaBatteryHalf } from 'react-icons/fa';
import DashboardLayout from '../../layouts/DashboardLayout';
import { PageHeader, HeaderContent, StatusBadge } from '../../components/styled/CommonStyles';
import useGoogleMapsLoader from '../leads/route-planner/useGoogleMapsLoader';
import { getLive, activityColor } from './timeTrackingService';
import { formatRelativeTime } from '../communications/utils/formatUtils';

const REFRESH_MS = 45000;
const n = (v) => (v === null || v === undefined ? null : Number(v));

const Layout = styled.div`
  display: grid; grid-template-columns: 2fr 1fr; gap: ${(p) => p.theme.spacing(3)};
  @media (max-width: 900px) { grid-template-columns: 1fr; }
`;
const MapBox = styled.div`
  height: 560px; border-radius: ${(p) => p.theme.borderRadius}; overflow: hidden;
  background: ${(p) => p.theme.colors.surface};
`;
const Side = styled.div`display: flex; flex-direction: column; gap: ${(p) => p.theme.spacing(2)};`;
const PersonCard = styled.div`
  background: ${(p) => p.theme.colors.surface}; border-radius: ${(p) => p.theme.borderRadius};
  padding: ${(p) => p.theme.spacing(2)}; box-shadow: 0 2px 4px rgba(0,0,0,.1);
  .name { color: ${(p) => p.theme.colors.textPrimary}; font-weight: 600; }
  .meta { color: ${(p) => p.theme.colors.textSecondary}; font-size: ${(p) => p.theme.typography.fontSize.small}; margin-top: 4px;
    display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
`;
const RefreshBtn = styled.button`
  background: ${(p) => p.theme.colors.primary}; color: ${(p) => p.theme.colors.black};
  border: none; border-radius: ${(p) => p.theme.borderRadius}; cursor: pointer;
  padding: ${(p) => p.theme.spacing(1)} ${(p) => p.theme.spacing(2)}; display: flex; align-items: center; gap: 8px; font-weight: 500;
`;
const Empty = styled.p`color: ${(p) => p.theme.colors.textSecondary}; text-align: center; padding: ${(p) => p.theme.spacing(3)};`;

const LiveMap = () => {
  const { isLoaded, error } = useGoogleMapsLoader();
  const [active, setActive] = useState([]);
  const mapRef = useRef(null);
  const mapObj = useRef(null);
  const markers = useRef([]);

  const load = useCallback(() => {
    getLive().then((d) => setActive(d.active || [])).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => clearInterval(t);
  }, [load]);

  // (Re)draw markers whenever the map is ready or the data changes.
  useEffect(() => {
    if (!isLoaded || !mapRef.current || !window.google?.maps) return;
    const gm = window.google.maps;
    if (!mapObj.current) {
      mapObj.current = new gm.Map(mapRef.current, {
        zoom: 11, center: { lat: 42.2626, lng: -71.8023 },
        mapTypeControl: false, streetViewControl: false, fullscreenControl: false,
      });
    }
    markers.current.forEach((m) => m.setMap(null));
    markers.current = [];
    const bounds = new gm.LatLngBounds();
    const iw = new gm.InfoWindow();

    active.forEach((a) => {
      const p = a.lastPing;
      if (!p || p.latitude == null) return;
      const pos = { lat: n(p.latitude), lng: n(p.longitude) };
      const marker = new gm.Marker({
        map: mapObj.current, position: pos, title: a.user?.username || 'Employee',
        icon: {
          path: gm.SymbolPath.CIRCLE, scale: 9,
          fillColor: activityColor(p.activity), fillOpacity: 1,
          strokeColor: '#ffffff', strokeWeight: 2,
        },
      });
      marker.addListener('click', () => {
        iw.setContent(
          `<div style="color:#000;font-size:13px">
             <b>${a.user?.username || 'Employee'}</b><br/>
             ${p.activity || 'unknown'} &middot; ${formatRelativeTime(p.recorded_at)}
           </div>`
        );
        iw.open(mapObj.current, marker);
      });
      markers.current.push(marker);
      bounds.extend(pos);
    });

    if (!bounds.isEmpty()) {
      mapObj.current.fitBounds(bounds);
      gm.event.addListenerOnce(mapObj.current, 'idle', () => {
        if (mapObj.current.getZoom() > 15) mapObj.current.setZoom(15);
      });
    }
  }, [isLoaded, active]);

  return (
    <DashboardLayout>
      <PageHeader>
        <HeaderContent>
          <h1>Live Map</h1>
          <RefreshBtn onClick={load}><FaSyncAlt /> Refresh</RefreshBtn>
        </HeaderContent>
      </PageHeader>

      <Layout>
        <MapBox>
          {error ? <Empty style={{ color: '#FF5252' }}>Map failed to load.</Empty>
            : !isLoaded ? <Empty>Loading map…</Empty>
              : <div ref={mapRef} style={{ width: '100%', height: '100%' }} />}
        </MapBox>

        <Side>
          <PageHeader style={{ margin: 0 }}>
            <HeaderContent><h1 style={{ fontSize: '1.3em' }}><FaUserClock /> Clocked in ({active.length})</h1></HeaderContent>
          </PageHeader>
          {active.length === 0 ? <Empty>No one is clocked in right now.</Empty> : active.map((a) => (
            <PersonCard key={a.shift?.id}>
              <div className="name">{a.user?.username || `#${a.shift?.user_id}`}</div>
              <div className="meta">
                {a.lastPing ? (
                  <>
                    <StatusBadge color={activityColor(a.lastPing.activity)}>{a.lastPing.activity || 'unknown'}</StatusBadge>
                    <span>{formatRelativeTime(a.lastPing.recorded_at)}</span>
                    {a.lastPing.battery != null && <span><FaBatteryHalf /> {a.lastPing.battery}%</span>}
                  </>
                ) : <span>No location yet</span>}
              </div>
              {a.segment?.label && <div className="meta">At: {a.segment.label}</div>}
            </PersonCard>
          ))}
        </Side>
      </Layout>
    </DashboardLayout>
  );
};

export default LiveMap;
